import {
  BasicButton,
  ButtonGroup,
  Checkbox,
  CheckboxGroup,
  DialogTrigger,
  Divider,
  GhostButton,
  Popover,
  PopoverContent,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { APP_IDENTIFIER } from "../../constants";
import { AppIdentifier, CheckResult } from "../types";

export type FetchPdfOptions = {
  checkIds?: string[];
  excludeOverview?: boolean;
};

interface PageHeaderProps {
  name?: string;
  isPDF?: boolean;
  fetchPDF?: (options?: FetchPdfOptions) => Promise<void>;
  isDownloading?: boolean;
  isLoading?: boolean;
  checks?: CheckResult[];
  appIdentifier?: AppIdentifier;
}

function areSameIdSets(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

export function PageHeader({
  name,
  isPDF = false,
  fetchPDF,
  isDownloading,
  isLoading,
  checks = [],
  appIdentifier,
}: PageHeaderProps) {
  const { t } = useTranslation();
  const allCheckIds = useMemo(() => checks.map((check) => check.id), [checks]);
  const checkIdsKey = useMemo(
    () => allCheckIds.slice().sort().join(","),
    [allCheckIds]
  );
  const [selectedIds, setSelectedIds] = useState<string[]>(allCheckIds);
  const [includeOverview, setIncludeOverview] = useState(true);
  const shouldShowSectionFilter =
    (appIdentifier === APP_IDENTIFIER.CLIENT_PORTAL ||
      appIdentifier === APP_IDENTIFIER.CASE_MANAGEMENT) &&
    checks.length > 0;

  useEffect(() => {
    setSelectedIds(allCheckIds);
    setIncludeOverview(true);
    // Reset only when the ID set changes, not when `checks` is a new array with the same IDs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIdsKey]);

  const isAllSelected = areSameIdSets(selectedIds, allCheckIds);
  const hasSelection = selectedIds.length > 0;
  const shouldUseReportDownloadLabels =
    appIdentifier === APP_IDENTIFIER.CLIENT_PORTAL ||
    appIdentifier === APP_IDENTIFIER.CASE_MANAGEMENT;
  const isFullReportDownload = isAllSelected && includeOverview;
  const downloadLabel = shouldUseReportDownloadLabels
    ? isFullReportDownload
      ? t("Download Report")
      : t("Download Partial Report")
    : t("Download PDF");

  const handleDownload = () => {
    // CP/CM: section picker. Full selection omits check_ids (full PDF).
    if (shouldShowSectionFilter) {
      if (!hasSelection) {
        return;
      }
      const options: FetchPdfOptions = {};
      if (!isAllSelected) {
        options.checkIds = selectedIds;
      }
      if (!includeOverview) {
        options.excludeOverview = true;
      }
      if (options.checkIds || options.excludeOverview) {
        fetchPDF?.(options);
        return;
      }
      fetchPDF?.();
      return;
    }
    // AP (and no picker): generate-report has no check_ids; always full PDF.
    fetchPDF?.();
  };

  return (
    <div className={styles.top}>
      {isPDF ? (
        <div className={styles.pdfTitleContainer}>
          <Text
            as="div"
            testID="page-header-pdf-title"
            className={styles.pdfTitleText}
          >
            {t("Background check for")}
          </Text>
          <Text
            as="div"
            testID="page-header-pdf-title-name"
            className={styles.pdfTitleText}
          >
            {name}
          </Text>
        </div>
      ) : (
        <>
          <Text as="h2" testID="page-header-title" style="global/heading-large">
            {t("CASE Report", "Report")}
          </Text>
          <ButtonGroup>
            {shouldShowSectionFilter && (
              <DialogTrigger>
                <GhostButton
                  size="medium"
                  aria-label={t("Report Sections")}
                  rightIcon="expand_more"
                  testID="section-filter-trigger"
                >
                  <Text color="text-weak">{t("Report Sections")}</Text>
                </GhostButton>
                <Popover placement="bottom end">
                  <PopoverContent testID="section-filter-popover">
                    <div className={styles.sectionFilter}>
                      <Checkbox
                        isSelected={includeOverview}
                        onChange={setIncludeOverview}
                        testID="section-filter-overview"
                      >
                        {t("Overview")}
                      </Checkbox>
                      <Divider testID="section-filter-divider" />
                      <CheckboxGroup
                        aria-label={t("Report Sections")}
                        value={selectedIds}
                        onChange={(values) => setSelectedIds(values)}
                        testID="section-filter-checkboxes"
                      >
                        {checks.map((check) => (
                          <Checkbox
                            key={check.id}
                            value={check.id}
                            testID={`section-filter-${check.id}`}
                          >
                            {check.type_display}
                          </Checkbox>
                        ))}
                      </CheckboxGroup>
                    </div>
                  </PopoverContent>
                </Popover>
              </DialogTrigger>
            )}
            <BasicButton
              leftIcon="download"
              aria-label={downloadLabel}
              testID="download-pdf-button"
              onPress={handleDownload}
              isDisabled={
                isLoading || (shouldShowSectionFilter && !hasSelection)
              }
              isLoading={isDownloading}
            >
              {downloadLabel}
            </BasicButton>
          </ButtonGroup>
        </>
      )}
    </div>
  );
}

const styles = {
  top: css({
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "var(--space-4)",
  }),
  sectionFilter: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  pdfTitleText: css({
    fontSize: "40px",
    fontWeight: 600,
    lineHeight: "52px",
  }),
  pdfTitleContainer: css({
    paddingTop: "var(--space-8)",
    paddingBottom: "var(--space-6)",
    background:
      "radial-gradient(circle at center, var(--background-accent-neutral-weakest) 54%, transparent 0%)",
    backgroundRepeat: "repeat",
    backgroundSize: "54px 54px",
    backgroundPositionX: "-7px",
    width: "100%",
  }),
};
